import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Cloud, Workflow, Code, Container, Layers, Shield, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/seo/SEO';
import content from '../content.json';

const iconMap = {
  Cloud,
  Workflow,
  Code,
  Container,
  Layers,
  Shield,
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

export default function Home() {
  const { hero, intro, stats } = content.home;
  const services = content.services.items.slice(0, 3);

  return (
    <>
      <SEO 
        title={content.seo.home.title}
        description={content.seo.home.description}
        canonical="https://dakshtechinfo.uk"
      />
      
      <div data-testid="home-page">
        {/* Hero Section */}
        <section 
          className="relative min-h-[90vh] flex items-center overflow-hidden"
          data-testid="hero-section"
        >
          {/* Background */}
          <div className="absolute inset-0 z-0">
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ 
                backgroundImage: `url(${content.image_urls?.hero_background?.url || 'https://images.unsplash.com/photo-1758637612717-e0cd7dc3cdea?crop=entropy&cs=srgb&fm=jpg&q=85'})`,
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/80 to-background" />
          </div>

          <div className="container-custom relative z-10 py-20 md:py-32">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="max-w-4xl"
            >
              <motion.div variants={itemVariants} className="mb-6">
                <span className="inline-flex items-center gap-2 text-sm font-medium text-accent bg-accent/10 px-4 py-2 rounded-full">
                  <span className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                  Cloud & DevOps Excellence
                </span>
              </motion.div>
              
              <motion.h1 
                variants={itemVariants}
                className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-4"
              >
                {hero.title}
                <br />
                <span className="text-gradient">{hero.subtitle}</span>
              </motion.h1>
              
              <motion.p 
                variants={itemVariants}
                className="text-base md:text-lg text-muted-foreground max-w-2xl mb-8"
              >
                {hero.description}
              </motion.p>
              
              <motion.div 
                variants={itemVariants}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Button 
                  size="lg" 
                  asChild
                  className="group"
                  data-testid="hero-cta-primary"
                >
                  <Link to="/contact">
                    {hero.cta_primary}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  asChild
                  data-testid="hero-cta-secondary"
                >
                  <Link to="/services">{hero.cta_secondary}</Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 md:py-16 border-y border-border bg-muted/30">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12"
            >
              {stats.map((stat, index) => (
                <motion.div 
                  key={index}
                  variants={itemVariants}
                  className="text-center"
                  data-testid={`stat-${index}`}
                >
                  <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-accent mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="section-padding" data-testid="intro-section">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="max-w-3xl mx-auto text-center"
            >
              <motion.h2 
                variants={itemVariants}
                className="text-3xl md:text-4xl font-bold mb-6"
              >
                {intro.title}
              </motion.h2>
              <motion.p 
                variants={itemVariants}
                className="text-base md:text-lg text-muted-foreground"
              >
                {intro.description}
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Services Preview */}
        <section className="section-padding bg-muted/30" data-testid="services-preview">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              <motion.div variants={itemVariants} className="text-center mb-12 md:mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  {content.services.title}
                </h2>
                <p className="text-base text-muted-foreground max-w-2xl mx-auto">
                  {content.services.subtitle}
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {services.map((service, index) => {
                  const Icon = iconMap[service.icon] || Cloud;
                  return (
                    <motion.div key={service.id} variants={itemVariants}>
                      <Card 
                        className="h-full hover-lift border-border/50 bg-card"
                        data-testid={`service-card-${service.id}`}
                      >
                        <CardContent className="p-6 md:p-8">
                          <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                            <Icon className="h-6 w-6 text-accent" />
                          </div>
                          <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            {service.description}
                          </p>
                          <Link 
                            to="/services"
                            className="inline-flex items-center text-sm text-accent font-medium hover:underline"
                          >
                            Learn more <ChevronRight className="ml-1 h-4 w-4" />
                          </Link>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>

              <motion.div variants={itemVariants} className="text-center mt-12">
                <Button variant="outline" size="lg" asChild data-testid="view-all-services">
                  <Link to="/services">
                    View All Services
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding" data-testid="cta-section">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="relative overflow-hidden rounded-2xl bg-primary p-8 md:p-12 lg:p-16"
            >
              <div className="relative z-10 max-w-2xl">
                <motion.h2 
                  variants={itemVariants}
                  className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary-foreground mb-4"
                >
                  Ready to Transform Your Infrastructure?
                </motion.h2>
                <motion.p 
                  variants={itemVariants}
                  className="text-base text-primary-foreground/80 mb-8"
                >
                  Let's discuss how DakshTechInfo can help you achieve your cloud and DevOps goals.
                </motion.p>
                <motion.div variants={itemVariants}>
                  <Button 
                    size="lg" 
                    variant="secondary" 
                    asChild
                    className="group"
                    data-testid="cta-contact"
                  >
                    <Link to="/contact">
                      Schedule a Consultation
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </motion.div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/20 rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-[#D4AF37]/10 rounded-full blur-2xl" />
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
